import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contractApi } from '@/features/contracts/api/contractApi';
import { notifications } from '@/utils/notifications';

/**
 * Lister les brouillons de l'utilisateur courant
 */
export const useDraftsList = () => {
  return useQuery({
    queryKey: ['contract-drafts'],
    queryFn: () => contractApi.getDrafts(),
    staleTime: 30000,
  });
};

/**
 * Charger un brouillon par UUID
 */
export const useLoadDraft = (uuid: string | undefined) => {
  return useQuery({
    queryKey: ['contract-draft', uuid],
    queryFn: () => contractApi.getDraft(uuid!),
    enabled: !!uuid,
  });
};

/**
 * Sauvegarder un nouveau brouillon
 */
export const useSaveDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { formData: Record<string, any>; currentStep: number; label?: string }) =>
      contractApi.saveDraft(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-drafts'] });
      notifications.success('Brouillon sauvegardé');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la sauvegarde du brouillon';
      notifications.error(message);
    },
  });
};

/**
 * Mettre à jour un brouillon existant
 */
export const useUpdateDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, ...payload }: { uuid: string; formData?: Record<string, any>; currentStep?: number; label?: string }) =>
      contractApi.updateDraft(uuid, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['contract-drafts'] });
      queryClient.invalidateQueries({ queryKey: ['contract-draft', variables.uuid] });
      notifications.success('Brouillon mis à jour');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour du brouillon';
      notifications.error(message);
    },
  });
};

/**
 * Supprimer un brouillon
 */
export const useDeleteDraft = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid: string) => contractApi.deleteDraft(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contract-drafts'] });
      notifications.success('Brouillon supprimé');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Erreur lors de la suppression du brouillon';
      notifications.error(message);
    },
  });
};
